import { useState } from "react";
import { ArrowRight, Mail, MailCheck, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const { solicitarLink } = useAuth();
  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [enviado, setEnviado] = useState(null);

  const valido = EMAIL_RE.test(email.trim());

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valido || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const data = await solicitarLink(email.trim().toLowerCase());
      setEnviado({
        email: data?.sentTo || email.trim().toLowerCase(),
        esNueva: !!data?.esNueva,
        expiresInMinutes: data?.expiresInMinutes ?? 15,
        emailEnviado: data?.emailEnviado !== false,
        devLink: data?.devLink || null,
      });
    } catch (err) {
      console.error("login_request_link_error", err);
      const code = err?.response?.data?.error;
      const msg =
        code === "no_se_pudo_enviar_email"
          ? "No se pudo enviar el email. Inténtalo en unos minutos."
          : code === "email_invalido"
            ? "El email no es válido"
            : "No se pudo solicitar el enlace de acceso";
      setError(msg);
    } finally {
      setEnviando(false);
    }
  };

  const reenviar = async () => {
    if (!enviado || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const data = await solicitarLink(enviado.email);
      setEnviado({
        email: data?.sentTo || enviado.email,
        esNueva: enviado.esNueva,
        expiresInMinutes: data?.expiresInMinutes ?? 15,
        emailEnviado: data?.emailEnviado !== false,
        devLink: data?.devLink || null,
      });
    } catch (err) {
      console.error("login_resend_link_error", err);
      setError("No se pudo reenviar el email. Inténtalo más tarde.");
    } finally {
      setEnviando(false);
    }
  };

  const usarOtro = () => {
    setEnviado(null);
    setError(null);
    setEmail("");
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="rx-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-md bg-blue-600 text-white flex items-center justify-center font-bold font-display">
              R
            </div>
            <div>
              <div className="font-display font-bold text-base leading-none">Reformas</div>
              <div className="text-xs text-slate-500 mt-1">Express</div>
            </div>
          </div>

          {enviado ? (
            <div data-testid="login-sent">
              <div className="inline-flex w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 items-center justify-center mb-4">
                <MailCheck className="w-6 h-6" />
              </div>
              <h1 className="font-display font-bold text-2xl tracking-tight mb-2">
                Revisa tu bandeja de entrada
              </h1>
              <p className="text-sm text-slate-600 mb-1">
                Te hemos enviado un enlace de acceso a:
              </p>
              <p className="font-semibold text-slate-900 break-all mb-4">
                {enviado.email}
              </p>
              <ul className="text-sm text-slate-600 space-y-1.5 mb-4 list-disc pl-5">
                <li>Pulsa el botón del email para iniciar sesión.</li>
                <li>
                  El enlace caduca en{" "}
                  <strong>{enviado.expiresInMinutes} minutos</strong> y solo
                  puede usarse una vez.
                </li>
                <li>Si no lo ves, revisa también la carpeta de spam.</li>
              </ul>

              {!enviado.emailEnviado && enviado.devLink ? (
                <div
                  className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3"
                  data-testid="login-dev-link"
                >
                  <p className="font-bold text-amber-800 mb-1">
                    Modo desarrollo · email no entregado
                  </p>
                  <p className="text-amber-700 mb-2">
                    El proveedor rechazó el envío (probablemente sandbox o
                    email no autorizado). Usa este enlace para continuar:
                  </p>
                  <a
                    href={enviado.devLink}
                    className="text-blue-700 underline break-all"
                  >
                    {enviado.devLink}
                  </a>
                </div>
              ) : null}

              {error ? (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3">
                  {error}
                </div>
              ) : null}

              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={reenviar}
                  disabled={enviando}
                  variant="outline"
                  className="flex-1 border-slate-300 gap-2"
                  data-testid="login-resend"
                >
                  <RefreshCw className={`w-4 h-4 ${enviando ? "animate-spin" : ""}`} />
                  {enviando ? "Reenviando…" : "Reenviar email"}
                </Button>
                <Button
                  type="button"
                  onClick={usarOtro}
                  variant="ghost"
                  className="flex-1 text-slate-600"
                  data-testid="login-change-email"
                >
                  Usar otro email
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-display font-bold text-2xl tracking-tight mb-2">
                Accede a tu cuenta
              </h1>
              <p className="text-sm text-slate-500 mb-6">
                Introduce tu email y te enviaremos un enlace para entrar. Si es
                la primera vez, te creamos la cuenta automáticamente.
              </p>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoFocus
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@empresa.com"
                      className="pl-9 h-11"
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                {error ? (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {error}
                  </div>
                ) : null}

                <Button
                  type="submit"
                  disabled={!valido || enviando}
                  className="w-full bg-blue-600 hover:bg-blue-700 h-11 text-white gap-2"
                  data-testid="login-submit"
                >
                  {enviando ? "Enviando…" : "Enviar enlace de acceso"}
                  {!enviando ? <ArrowRight className="w-4 h-4" /> : null}
                </Button>
              </form>

              <p className="text-[11px] text-slate-400 mt-6 leading-relaxed text-center">
                Recibirás un email con un enlace seguro. La cuenta se asocia al
                email para que distintos usuarios trabajen por separado.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
